// Client-side max-duration validation for catalog items.
//
// Each MFSelectableResource carries a `maxTimeValue + maxTimeUnit` pair
// (and an `allowIndefinite` escape hatch). The bulk modal sets one duration
// for all items, so we need to flag — up front, before submission — any
// resource the user couldn't actually get for that long. The server still
// enforces this at submit time; this is a UX guardrail, not a security
// boundary.

import type { DurationValue } from "components/ds/DurationField";
import type { DurationPreset } from "components/ds/DurationChipGroup";
import {
  capDuration,
  exceedsCap,
  longestSeconds,
  type UnitAmount,
} from "state/members/access_v2/durationCaps";
import { durationLabel } from "components/ds/durationLabel";

// A resource's own limits, or the combined terms of the roles ticked on it.
export type DurationTerms = {
  allowIndefinite: boolean;
  indefiniteOnly?: boolean | null;
  maxTimeUnit?: string | null;
  maxTimeValue?: number | null;
};

const pluralise = (unit: string, n: number): string => {
  const base = unit.replace(/s$/, "");
  return n === 1 ? base : `${base}s`;
};

export interface DurationCheck {
  exceeds: boolean;
  /** Human-readable message for the tooltip when `exceeds=true`. */
  reason?: string;
}

// Hard cap mirroring what's sane in practice — 5 years. Anything past this
// is almost certainly an input error (e.g. "9999 months"). Even if the
// server would technically accept it on an indefinite-allowed resource,
// we flag it so the user can't accidentally request a near-permanent grant.
const SANITY_CAP = { num: 5, unit: "year" };

// Does `requested` exceed what the resource policy allows? Mirrors the
// server's `validate_duration` cases in create_access_request.ex so the
// client warns up front instead of letting submission fail:
//
//   * allowIndefinite + no cap          → ok (unless > sanity cap)
//   * allowIndefinite + cap             → ok (indefinite trumps)
//   * !allowIndefinite + no cap         → reject (server says
//                                          "Maximum duration not configured")
//   * !allowIndefinite + cap            → compare
export const checkDurationAgainstMax = (
  requested: DurationValue,
  resource: DurationTerms,
): DurationCheck => {
  // An explicit indefinite request is valid only where the resource allows it
  // (the bulk/per-item pickers only offer "Indefinite" on allowing resources;
  // this guards the mixed-basket case where a bulk "Indefinite" lands on a row
  // that doesn't allow it). No sanity cap — indefinite is the deliberate choice.
  if (requested.indefinite) {
    return resource.allowIndefinite
      ? { exceeds: false }
      : {
          exceeds: true,
          reason: "This resource doesn't allow indefinite access.",
        };
  }

  if (resource.indefiniteOnly) {
    return {
      exceeds: true,
      reason: "This resource can only be requested indefinitely.",
    };
  }

  // Sanity cap — applies even when allowIndefinite=true. Catches absurd
  // inputs like "9999 months" that would land grants effectively forever.
  if (exceedsCap(requested, SANITY_CAP)) {
    return {
      exceeds: true,
      reason:
        "Requested duration is unusually long. Request access for the period you actually need.",
    };
  }

  if (resource.allowIndefinite) return { exceeds: false };

  // No max configured but indefinite isn't allowed — the server rejects
  // this with "Maximum access duration is not configured for this
  // resource". Surface it client-side too so the user isn't surprised.
  if (!resource.maxTimeValue || !resource.maxTimeUnit) {
    return {
      exceeds: true,
      reason: "Max access duration is not configured for this resource.",
    };
  }

  if (
    !exceedsCap(requested, {
      num: resource.maxTimeValue,
      unit: String(resource.maxTimeUnit),
    })
  )
    return { exceeds: false };

  return {
    exceeds: true,
    reason: `Max allowed access duration is ${resource.maxTimeValue} ${pluralise(
      String(resource.maxTimeUnit),
      resource.maxTimeValue,
    )}.`,
  };
};

const finiteCaps = (resources: DurationTerms[]): UnitAmount[] =>
  resources
    .flatMap((r) =>
      r.allowIndefinite || !r.maxTimeValue || !r.maxTimeUnit
        ? []
        : [{ num: r.maxTimeValue, unit: String(r.maxTimeUnit) }],
    )
    .filter((cap) => longestSeconds(cap) > 0);

// A month and 30 days each outlast the other in some month; the shorter at
// its longest wins then.
const tighter = (a: UnitAmount, b: UnitAmount): boolean =>
  exceedsCap(b, a) &&
  (!exceedsCap(a, b) || longestSeconds(a) < longestSeconds(b));

const tightestCap = (caps: UnitAmount[]): UnitAmount | null =>
  caps.reduce<UnitAmount | null>(
    (best, cap) => (!best || tighter(cap, best) ? cap : best),
    null,
  );

const fitsEvery = (d: UnitAmount, caps: UnitAmount[]): boolean =>
  caps.every((cap) => !exceedsCap(d, cap));

const sameLength = (a: UnitAmount, b: UnitAmount): boolean =>
  !exceedsCap(a, b) && !exceedsCap(b, a);

// The smallest (most restrictive) max across the given resources, as a picker
// DurationValue — used to seed the bulk duration so the default already fits
// every selected item. Resources with no finite cap (allowIndefinite, or no
// configured max) don't constrain and are skipped, as are caps no picker
// duration fits under. Returns null when nothing constrains, so the caller
// keeps its own default.
export const smallestMaxDuration = (
  resources: DurationTerms[],
): DurationValue | null => {
  const cap = tightestCap(
    finiteCaps(resources).filter((c) => capDuration(c.num, c.unit)),
  );
  return cap && capDuration(cap.num, cap.unit);
};

// Trim a preset list to the basket's caps, so the bulk picker only suggests
// durations every selected item can actually get. The most restrictive cap is
// appended as a chip when no surviving preset already lasts exactly as long
// (so a 24-hour cap doesn't duplicate a "1 day" preset) — e.g. a 3-hour cap
// yields just "3 hours", a 2-week cap yields "4 hours / 1 day / 1 week /
// 2 weeks". Uncapped baskets (allowIndefinite or no configured max on every
// item) keep the full list. Anything longer stays reachable via the Custom
// picker, where the row warnings take over.
export const capDurationPresets = (
  presets: DurationPreset[],
  resources: DurationTerms[],
): DurationPreset[] => {
  // No finite duration fits an indefinite-only resource, so suggest none.
  if (resources.some((r) => r.indefiniteOnly)) return [];

  const caps = finiteCaps(resources);
  const tightest = tightestCap(caps);
  if (!tightest) return presets;

  const within = presets.filter(
    (p) => !p.value.indefinite && fitsEvery(p.value, caps),
  );

  const capValue = capDuration(tightest.num, tightest.unit);
  if (
    capValue &&
    fitsEvery(capValue, caps) &&
    !within.some((p) => sameLength(p.value, capValue))
  ) {
    within.push({
      label: durationLabel(capValue),
      value: capValue,
    });
  }

  return within;
};

// Whether a BULK duration picker may offer "Indefinite" for the given
// resources. Bulk applies to every item at once, so a single resource that
// disallows indefinite must hide the option — otherwise a bulk "Indefinite"
// would flag those rows as "won't be sent". Empty list → false (nothing to
// grant). Mixed baskets pick indefinite per-row via "Customise individual
// resources" instead.
export const allResourcesAllowIndefinite = (
  resources: Pick<DurationTerms, "allowIndefinite">[],
): boolean => resources.length > 0 && resources.every((r) => r.allowIndefinite);

// Whether Indefinite is the one bulk duration that fits the whole basket: some
// resource accepts nothing else, and every resource allows it. Such a basket
// defaults to Indefinite and offers no Custom duration.
export const basketIndefiniteOnly = (
  resources: Pick<DurationTerms, "allowIndefinite" | "indefiniteOnly">[],
): boolean =>
  resources.some((r) => r.indefiniteOnly) &&
  allResourcesAllowIndefinite(resources);

// Same duration, indefinite included: 3 months and "Indefinite" (which still
// carries a num/unit for the picker) are different choices.
export const sameDuration = (a: DurationValue, b: DurationValue): boolean =>
  a.num === b.num &&
  a.unit === b.unit &&
  Boolean(a.indefinite) === Boolean(b.indefinite);
